import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import {
  useNavigator,
  OwnerRoutes,
  UniversalRoutes,
  type MinArrayType,
} from "@/routers/routes";
import { useUser } from "@/UserContext";
import { RenderTrainerDetail } from "./TrainerDetail";
import type { TrainerDetailsSessionTypeOutput } from "@/api-types";

export const TrainerPage = () => {
  const { trainerId } = useParams();
  const { navigate } = useNavigator();
  const user = useUser();

  // Use public endpoint for all users, then get session types if user is owner
  const { data: trainer } = client.useQuery(
    "get",
    "/public/trainers/{trainerId}",
    {
      params: {
        path: { trainerId: trainerId!, trainerName: null },
      },
    },
  );

  // Get session types for all users - try owner endpoint first, fallback for trainers viewing their own profile
  const { data: sessionTypes } = client.useQuery(
    "get",
    "/owner/trainer/{trainerId}/session-types",
    {
      params: {
        path: { trainerId: trainerId! },
      },
    },
    {
      enabled: !!trainer,
    },
  );

  // Move mutation hook before any conditional returns
  const createEngagementMutation = client.useMutation(
    "post",
    "/owner/engagements/",
  );

  if (!trainer || !sessionTypes) {
    return <FancyLoader />;
  }

  const handleBookSession = (sessionTypeId?: string) => {
    if (user.role === "owner") {
      const queryParams = sessionTypeId
        ? `?sessionTypeId=${sessionTypeId}`
        : "";
      const path = `${OwnerRoutes.REQUEST_SESSION.path.replace(":trainerId", trainer.id)}${queryParams}`;
      navigate({
        path: path,
      });
    }
    // Trainers can't book sessions - do nothing
  };

  const handleAskQuestion = async () => {
    if (user.role === "owner") {
      // Create engagement first
      const result = await createEngagementMutation.mutateAsync({
        params: {
          query: {
            trainerId: trainer.id,
          },
        },
      });
      navigate({
        path: UniversalRoutes.CHAT.DETAIL,
        params: { engagementId: result.engagementId },
      });
    }
    // Trainers can't initiate chats with other trainers - do nothing
  };

  return (
    <RenderTrainerDetail
      trainer={trainer}
      sessionTypes={
        sessionTypes as MinArrayType<TrainerDetailsSessionTypeOutput>
      }
      onBookSession={handleBookSession}
      onAskQuestion={handleAskQuestion}
      isPreview={user.role === "trainer"}
    />
  );
};
